import { SignalContext } from "context/SignalContext";
import { useContext, useEffect } from "react";
import { ActRequestType } from "webrtc-player/common/base";
import { v4 as uuidv4 } from "uuid";
import { AuthContext } from "context/AuthContext";
import { Signal } from "webrtc-player/common/signal";
import { AppError } from "webrtc-player/common/app_error";
import { errorHandler } from "lib/utils";
const requestCache = new Map();
let wsWaiting = [];
export default function useWsSender() {
  const { signal, wsIsOpen, wsMsgData } = useContext(SignalContext);
  const { user } = useContext(AuthContext);
  useEffect(() => {
    if (!wsMsgData || !wsMsgData.result) {
      return;
    }
    const [resolve, reject] = requestCache.get(wsMsgData.result.id) || [];
    const error = wsMsgData.result.error;
    if (error) {
      let errStr;
      try {
        errStr = JSON.stringify(error);
      } catch (e) {
        errStr = error;
      }
      const appError = new AppError({
        name: error.name,
        code: error?.code,
        message: error?.message,
      });
      errorHandler(appError);
      reject?.(new Error(errStr));
    } else {
      wsMsgData.result.data.__wsRequestId = wsMsgData.result.id;
      resolve?.(wsMsgData.result.data);
    }
    requestCache.delete(wsMsgData.result.id);
  }, [wsMsgData]);
  useEffect(() => {
    if (signal && wsIsOpen && wsWaiting.length) {
      wsWaiting.forEach((item) => item(signal));
      wsWaiting = [];
    }
  }, [signal, wsIsOpen]);
  function waitForWs(): Promise<Signal> {
    return new Promise((resolve) => {
      if (wsIsOpen && signal) {
        resolve(signal);
      } else {
        wsWaiting.push(resolve);
      }
    });
  }
  async function sendWsRequest(
    act,
    data,
    options?: { timeout?: number; orgId?: number },
  ) {
    const requestId = uuidv4();
    const _signal: Signal = await waitForWs();
    return new Promise((resolve, reject) => {
      const message: ActRequestType = {
        request: {
          id: requestId,
          act,
          data,
        },
        headers: { orgId: options?.orgId ?? user.orgId },
      };
      requestCache.set(requestId, [resolve, reject]);
      setTimeout(() => {
        const appError = new AppError({
          name: "timeout",
          message: "ws timeout, requestId:" + requestId,
        });
        errorHandler(appError);
        reject(new Error("ws timeout, requestId:" + requestId));
        requestCache.delete(requestId);
      }, options?.timeout ?? 15000);
      _signal.sendMessage(message);
    });
  }
  return { sendWsRequest };
}
